import type { Doc } from './editor';

/**
 * Autosave on this device (IndexedDB), kept separately for each account that signs in here.
 * Uploaded images live as blob: URLs while editing; the Blobs are stored next to the JSON
 * and given fresh blob: URLs when the work is reopened.
 */
const LEGACY_DB = 'craftstudio';   // before accounts: one store for the whole device
const STORE = 'docs';
const KEY = 'current';
let dbName = '';                   // set by useAccount() before anything is read or saved

/** Which server copy this local work belongs to, and whether it has edits not yet on the server. */
/**
 * The open work's copy on the server. `owner` (0015): an admin is editing a member's work — saves go into the
 * owner's own work; kept on this device too, so a reload still says whose work it is.
 */
export interface ServerLink {
  id: string | null; version: number; dirty: boolean;
  owner?: { name: string; email: string; status: 'pending' | 'active' | 'disabled' };
}

export interface SavedWork {
  json: string; doc: Doc; assets: Array<{ url: string; blob: Blob }>; savedAt: number;
  server?: ServerLink;
}

export const assets = new Map<string, Blob>();
/**
 * blob: address → the server address it was uploaded to (src/sync/saver.ts). A screen that read a picture while it was
 * still blob: (แก้รูป) knows the autosave meanwhile only moved the same file to the server — not another file (BUG-35).
 */
export const uploadedAs = new Map<string, string>();

function open(name = dbName): Promise<IDBDatabase> {
  return new Promise((res, rej) => {
    if (!name) { rej(new Error('ยังไม่รู้ว่าเป็นบัญชีไหน จึงยังไม่บันทึกในเครื่อง')); return; }
    if (!('indexedDB' in window)) { rej(new Error('เบราว์เซอร์นี้ไม่มีที่เก็บข้อมูลในเครื่อง')); return; }
    const r = indexedDB.open(name, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STORE);
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error ?? new Error('เปิดที่เก็บข้อมูลไม่ได้'));
  });
}

function getRaw(db: IDBDatabase): Promise<SavedWork | undefined> {
  return new Promise((res, rej) => {
    const r = db.transaction(STORE).objectStore(STORE).get(KEY);
    r.onsuccess = () => res(r.result as SavedWork | undefined);
    r.onerror = () => rej(r.error ?? new Error('อ่านงานที่บันทึกไว้ไม่ได้'));
  });
}

function putRaw(db: IDBDatabase, value: SavedWork | null): Promise<void> {
  return new Promise((res, rej) => {
    const tx = db.transaction(STORE, 'readwrite');
    if (value) tx.objectStore(STORE).put(value, KEY); else tx.objectStore(STORE).delete(KEY);
    tx.oncomplete = () => res();
    tx.onerror = () => rej(tx.error ?? new Error('บันทึกไม่สำเร็จ'));
    tx.onabort = () => rej(tx.error ?? new Error('ยกเลิกการบันทึก'));
  });
}

/**
 * Point local saving at this account's own store. Work saved on this device before accounts existed
 * moves to the first account that signs in here (once), so it is not lost and not shown to anyone else.
 * Returns true when that happened.
 */
export async function useAccount(userId: string): Promise<boolean> {
  dbName = `craftstudio-${userId}`;
  const mine = await open();
  if (await getRaw(mine)) return false;
  const legacy = await open(LEGACY_DB);
  const old = await getRaw(legacy);
  if (!old?.json) return false;
  await putRaw(mine, old);
  await putRaw(legacy, null);
  return true;
}

/** this account's local store name ('' before sign-in); the video clip store sits next to it (src/video/media.ts) */
export const accountStore = () => dbName;

export async function saveWork(json: string, doc: Doc, server?: ServerLink): Promise<void> {
  const used = [...assets.keys()].filter(u => json.includes(u)).map(u => ({ url: u, blob: assets.get(u)! }));
  const value: SavedWork = { json, doc: { ...doc }, assets: used, savedAt: Date.now(), server: server && { ...server } };
  await putRaw(await open(), value);
}

/** Loads the last work and rewrites its image URLs to fresh blob: URLs. */
export async function loadWork(): Promise<SavedWork | null> {
  const saved = await getRaw(await open());
  if (!saved?.json) return null;
  let json = saved.json;
  for (const a of saved.assets ?? []) {
    const u = URL.createObjectURL(a.blob);
    assets.set(u, a.blob);
    json = json.split(a.url).join(u);
  }
  return { ...saved, json };
}

/** A readable Thai reason for a failed save. */
export function saveErrorText(e: unknown): string {
  if (e instanceof DOMException && e.name === 'QuotaExceededError') return 'พื้นที่เก็บในเครื่องเต็ม ลองลบรูปขนาดใหญ่ออก';
  return e instanceof Error && e.message ? e.message : 'ไม่ทราบสาเหตุ';
}
